/**
 * Unified Color System
 *
 * Single source of truth for all colors across the application.
 * Replaces fragmented color definitions from Tailwind config, tokens.ts, and ui/index.tsx.
 *
 * @packageDocumentation
 */

// ============================================================================
// Brand Identity
// ============================================================================

export const brand = {
  primary: '#648cdc',      // Cool blue
  secondary: '#dcb48c',    // Warm gold
  tertiary: '#64b478',     // Muted green
} as const

// ============================================================================
// Backgrounds (Dark Theme)
// ============================================================================

export const background = {
  base: '#09090b',         // Body background
  elevated: '#0f0f18',     // Panels, cards
  surface: '#14141f',      // Interactive surfaces
  overlay: '#1a1a28',      // Modals, overlays
} as const

// ============================================================================
// Semantic States
// ============================================================================

export const semantic = {
  success: '#64b478',
  warning: '#dcb48c',
  error: '#dc6464',
  info: '#648cdc',
  neutral: '#808088',
} as const

// ============================================================================
// Text Hierarchy
// ============================================================================

export const text = {
  primary: '#fafafa',      // High emphasis
  secondary: '#b0b0b8',    // Medium emphasis
  tertiary: '#808088',     // Low emphasis
  disabled: '#505058',     // Disabled state
  accent: '#648cdc',       // Links, interactive
} as const

// ============================================================================
// Borders & Dividers
// ============================================================================

export const border = {
  subtle: '#1f1f2a',       // Low contrast
  default: '#2a2a38',      // Standard borders
  strong: '#3a3a48',       // High contrast
  accent: '#648cdc',       // Active/focused
} as const

// ============================================================================
// Interactive States (3D compatible - solid colors only)
// ============================================================================

export const interactive = {
  hover: '#1a1a28',
  active: '#2a2a38',
  focus: '#648cdc',
  disabled: '#14141f',
} as const

// ============================================================================
// Alpha Variants (HTML/2D only - NOT compatible with Three.js)
// ============================================================================

export const alpha = {
  white: {
    5: 'rgba(255, 255, 255, 0.05)',
    10: 'rgba(255, 255, 255, 0.10)',
    20: 'rgba(255, 255, 255, 0.20)',
    40: 'rgba(255, 255, 255, 0.40)',
    60: 'rgba(255, 255, 255, 0.60)',
    80: 'rgba(255, 255, 255, 0.80)',
  },
  primary: {
    10: 'rgba(100, 140, 220, 0.10)',
    20: 'rgba(100, 140, 220, 0.20)',
    40: 'rgba(100, 140, 220, 0.40)',
    60: 'rgba(100, 140, 220, 0.60)',
  },
} as const

// ============================================================================
// Glass/Frosted Surfaces (HTML/2D only)
// ============================================================================

export const glass = {
  background: 'rgba(15, 15, 24, 0.85)',
  border: 'rgba(255, 255, 255, 0.06)',
} as const

// ============================================================================
// 3D-Compatible Colors (for Three.js/pmndrs/uikit)
// ============================================================================

/**
 * Solid color values in hex number format for Three.js materials.
 * Use these for any 3D UI components (pmndrs/uikit).
 *
 * @example
 * ```tsx
 * import { colors3D } from '@/design-system'
 *
 * <Container backgroundColor={colors3D.brand.primary} />
 * ```
 */
export const colors3D = {
  brand: {
    primary: 0x648cdc,
    secondary: 0xdcb48c,
    tertiary: 0x64b478,
  },
  background: {
    base: 0x09090b,
    elevated: 0x0f0f18,
    surface: 0x14141f,
    overlay: 0x1a1a28,
  },
  semantic: {
    success: 0x64b478,
    warning: 0xdcb48c,
    error: 0xdc6464,
    info: 0x648cdc,
    neutral: 0x808088,
  },
  text: {
    primary: 0xfafafa,
    secondary: 0xb0b0b8,
    tertiary: 0x808088,
    disabled: 0x505058,
    accent: 0x648cdc,
  },
  border: {
    subtle: 0x1f1f2a,
    default: 0x2a2a38,
    strong: 0x3a3a48,
    accent: 0x648cdc,
  },
  interactive: {
    hover: 0x1a1a28,
    active: 0x2a2a38,
    focus: 0x648cdc,
    disabled: 0x14141f,
  },
} as const

// ============================================================================
// Color Utilities
// ============================================================================

/**
 * Convert hex string to Three.js number format
 * @example hexToNumber('#648cdc') → 0x648cdc
 */
export function hexToNumber(hex: string): number {
  return parseInt(hex.replace('#', '0x'))
}

/**
 * Convert Three.js number to hex string
 * @example numberToHex(0x648cdc) → '#648cdc'
 */
export function numberToHex(num: number): string {
  return `#${num.toString(16).padStart(6, '0')}`
}

// ============================================================================
// Exports
// ============================================================================

export const colors = {
  brand,
  background,
  semantic,
  text,
  border,
  interactive,
  alpha,
  glass,
} as const
